import { useTranslation } from 'react-i18next';

type TradingSpecRow = {
  symbol: string;
  contractUnit: string;
  minLot: string;
  maxLot: string;
  leverage: string;
  spreadType: string;
  avgSpread: string;
  swapLong: string;
  swapShort: string;
};

export default function TradingSpecsSection() {
  const { t } = useTranslation();

  const tradingSpecs: TradingSpecRow[] = [
    {
      symbol: 'XAUUSD',
      contractUnit: '100 oz',
      minLot: '0.01',
      maxLot: '100',
      leverage: '1:100',
      spreadType:  '45~65'+ '('+t('markets.spreadTypeFloating') +')',
      avgSpread: '~50',
      swapLong: '-72.73',
      swapShort: '44.04',
    },
    {
      symbol: 'XAGUSD',
      contractUnit: '5000 oz',
      minLot: '0.01',
      maxLot: '100',
      leverage: '1:20',
      spreadType: '80~100'+ '('+t('markets.spreadTypeFloating') +')',
      avgSpread: '~96',
      swapLong: '-16.384',
      swapShort: '0.536',
    },
    {
      symbol: 'GAUCNH',
      contractUnit: '1000 gau',
      minLot: '0.1',
      maxLot: '50',
      leverage: '1:50',
      spreadType: '40~70'+ '('+t('markets.spreadTypeFloating') +')',
      avgSpread: '~50',
      swapLong: '0',
      swapShort: '0',
    },
  ];

  const headers = [
    { key: 'symbol', label: t('markets.table.symbol') },
    { key: 'contractUnit', label: t('markets.table.contractUnit') },
    { key: 'minLot', label: t('markets.table.minLot') },
    { key: 'maxLot', label: t('markets.table.maxLot') },
    { key: 'leverage', label: t('markets.table.leverage') },
    { key: 'spreadType', label: t('markets.table.spreadType') },
    { key: 'avgSpread', label: t('markets.table.avgSpread') },
    { key: 'swapLong', label: t('markets.table.swapLong') },
    { key: 'swapShort', label: t('markets.table.swapShort') },
  ];

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            {t('markets.ts')}
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
            {t('markets.dtc')}
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-yellow-50 to-white">
            <h3 className="text-2xl font-bold text-gray-900">
              {t('markets.specsTitle')}
            </h3>
          </div>

          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {headers.map((h) => (
                  <th
                    key={h.key}
                    className="px-6 py-4 text-left text-sm font-bold text-gray-700"
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tradingSpecs.map((item, index) => (
                <tr
                  key={item.symbol}
                  className={`border-t border-gray-100 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                  } hover:bg-yellow-50/40 transition-colors`}
                >
                  <td className="px-6 py-5 font-bold text-gray-900">{item.symbol}</td>
                  <td className="px-6 py-5 text-gray-700">{item.contractUnit}</td>
                  <td className="px-6 py-5 text-gray-700">{item.minLot}</td>
                  <td className="px-6 py-5 text-gray-700">{item.maxLot}</td>
                  <td className="px-6 py-5">
                    <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 border border-blue-100">
                      {item.leverage}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-gray-700">{item.spreadType}</td>
                  <td className="px-6 py-5 text-gray-700">
                    {item.avgSpread === '--'
                      ? '--'
                      : `${item.avgSpread} ${t('markets.points')}`}
                  </td>
                  <td className="px-6 py-5 font-semibold text-red-600">{item.swapLong}</td>
                  <td className="px-6 py-5 font-semibold text-green-600">{item.swapShort}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="px-8 py-4 bg-gray-50 border-t border-gray-100">
            <p className="text-sm text-gray-500">{t('markets.specsDisclaimer')}</p>
          </div>
        </div>

        {/* Mobile / Tablet Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:hidden">
          {tradingSpecs.map((item) => (
            <div
              key={item.symbol}
              className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden"
            >
              <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-yellow-50 to-white">
                <div className="text-xl font-bold text-gray-900">{item.symbol}</div>
              </div>

              <div className="p-5 grid grid-cols-2 gap-4">
                <div className="col-span-2 bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">{t('markets.table.contractUnit')}</div>
                  <div className="text-base font-bold text-gray-900">{item.contractUnit}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">{t('markets.table.minLot')}</div>
                  <div className="text-base font-bold text-gray-900">{item.minLot}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">{t('markets.table.maxLot')}</div>
                  <div className="text-base font-bold text-gray-900">{item.maxLot}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">{t('markets.table.leverage')}</div>
                  <div className="text-base font-bold text-blue-700">{item.leverage}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">{t('markets.table.spreadType')}</div>
                  <div className="text-base font-bold text-gray-900">{item.spreadType}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">{t('markets.table.avgSpread')}</div>
                  <div className="text-base font-bold text-gray-900">
                    {item.avgSpread === '--'
                      ? '--'
                      : `${item.avgSpread} ${t('markets.points')}`}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">{t('markets.table.swapLong')}</div>
                  <div className="text-base font-bold text-red-600">{item.swapLong}</div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">{t('markets.table.swapShort')}</div>
                  <div className="text-base font-bold text-green-600">{item.swapShort}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 md:mt-10 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-3xl p-6 md:p-8 border border-yellow-200">
          <div className="flex items-start space-x-4">
            <div className="w-11 h-11 md:w-12 md:h-12 bg-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-white text-lg md:text-xl"></i>
            </div>
            <div>
              <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{t('markets.in')}</h4>
              <ul className="space-y-2 text-sm md:text-base text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                  {t('markets.smv')}
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                  {t('markets.sra')}
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                  {t('markets.mlm')}
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                  {t('markets.ata')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}